import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, XCircle, Phone, Eye, Receipt } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ListState from '../components/ListState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (s) => ({
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  pending_verification: 'bg-orange-50 text-orange-700 border-orange-200',
  form_submitted: 'bg-orange-50 text-orange-700 border-orange-200'
}[s] || 'bg-gray-50 text-gray-700 border-gray-200');

const statusLabel = (s) => ({
  approved: 'Approved',
  rejected: 'Rejected',
  pending_verification: 'Pending Verification',
  form_submitted: 'Pending Verification'
}[s] || s);

const TournamentRegistrations = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailReg, setDetailReg] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'approve' | 'reject', reg }

  const loadRegistrations = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/organizer/tournaments/${id}/registrations`, { withCredentials: true })
      .then(({ data }) => setRegistrations(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadRegistrations(); }, [loadRegistrations]);

  const openDetails = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/registrations/${rid}`, { withCredentials: true });
      setDetailReg(data);
    } catch (e) {
      window.alert('Failed to load registration details');
    }
  };

  const performAction = async () => {
    if (!confirmAction) return;
    const { type, reg } = confirmAction;
    try {
      await axios.post(`${API}/organizer/registrations/${reg.registration_id}/${type}`, {}, { withCredentials: true });
      setConfirmAction(null);
      setDetailReg(null);
      loadRegistrations();
    } catch (e) {
      window.alert(`Failed to ${type}`);
    }
  };

  const total = registrations.length;
  const approved = registrations.filter((r) => r.status === 'approved').length;
  const pending = registrations.filter((r) => r.status === 'pending_verification' || r.status === 'form_submitted').length;

  return (
    <>
      <SEO title="Tournament Registrations" description="Manage tournament registrations." path="/organizer/registrations" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/organizer')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-to-dashboard-link">
            <ArrowLeft size={16} weight="bold" /> Back to dashboard
          </button>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-8" data-testid="registrations-title">Registrations</h1>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">{total}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Total</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">{approved}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Approved</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-orange-500">{pending}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Pending</div>
            </div>
          </div>

          <ListState
            loading={loading}
            empty={total === 0}
            skeleton={<div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 rounded-2xl skeleton"></div>)}</div>}
            emptyState={
              <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
                <p className="text-gray-500">No registrations yet.</p>
              </div>
            }
          >
            <div className="space-y-3">
              {registrations.map((r, i) => (
                <div key={r.registration_id} className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={`registration-${i}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-bold text-gray-900" data-testid={`registration-name-${i}`}>{r.team_name || r.player_name || r.user_name}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${statusBadge(r.status)}`} data-testid={`registration-status-${i}`}>{statusLabel(r.status)}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        {r.captain_name && <span><span className="text-gray-400">Captain:</span> {r.captain_name}</span>}
                        <span className="flex items-center gap-1"><Phone size={12} /> {r.phone}</span>
                        {r.city && <span><span className="text-gray-400">City:</span> {r.city}</span>}
                        <span><span className="text-gray-400">Age:</span> {r.age_category}</span>
                        {typeof r.num_players === 'number' && r.num_players > 0 && (
                          <span><span className="text-gray-400">Players:</span> {r.num_players}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => openDetails(r.registration_id)}
                        className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded-lg text-xs font-semibold"
                        data-testid={`view-registration-btn-${i}`}
                      >
                        <Eye size={14} weight="bold" /> View
                      </button>
                      {(r.status === 'pending_verification' || r.status === 'form_submitted') && (
                        <>
                          <button
                            onClick={() => setConfirmAction({ type: 'approve', reg: r })}
                            className="inline-flex items-center gap-1.5 bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg text-xs font-semibold"
                            data-testid={`approve-btn-${i}`}
                          >
                            <CheckCircle size={14} weight="bold" /> Approve
                          </button>
                          <button
                            onClick={() => setConfirmAction({ type: 'reject', reg: r })}
                            className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-lg text-xs font-semibold"
                            data-testid={`reject-btn-${i}`}
                          >
                            <XCircle size={14} weight="bold" /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ListState>
        </div>
      </div>

      {/* Detail modal */}
      {detailReg && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl my-6" data-testid="registration-detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{detailReg.team_name || detailReg.player_name || detailReg.user_name}</h2>
                <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${statusBadge(detailReg.status)}`}>
                  {statusLabel(detailReg.status)}
                </span>
              </div>
              <button onClick={() => setDetailReg(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" aria-label="Close">×</button>
            </div>

            <div className="space-y-3 text-sm">
              {detailReg.team_name ? (
                <>
                  <Row label="Captain" value={detailReg.captain_name} />
                  <Row label="Captain Email" value={detailReg.captain_email} />
                </>
              ) : (
                <>
                  <Row label="Player Name" value={detailReg.player_name} />
                  <Row label="Email" value={detailReg.email || detailReg.user_email} />
                </>
              )}
              <Row label="Phone" value={detailReg.phone} />
              <Row label="City" value={detailReg.city} />
              <Row label="Age Category" value={detailReg.age_category} />
              {typeof detailReg.registration_fee_at_reg === 'number' && (
                <Row label="Fee at registration" value={detailReg.registration_fee_at_reg > 0 ? `₹${detailReg.registration_fee_at_reg}` : 'Free'} />
              )}

              {detailReg.players && detailReg.players.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Team Roster</div>
                  <ol className="list-decimal list-inside space-y-1 text-gray-800">
                    {detailReg.players.map((p, idx) => (
                      <li key={`player-${idx}-${p}`}>
                        <span className="font-medium">{p}</span>
                        {idx === 0 && <span className="ml-2 text-xs text-[#F97316] font-semibold">Captain</span>}
                      </li>
                    ))}
                  </ol>
                  {detailReg.substitutes && detailReg.substitutes.filter(Boolean).length > 0 && (
                    <>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-3 mb-2">Substitutes</div>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {detailReg.substitutes.filter(Boolean).map((s, idx) => <li key={`sub-${idx}-${s}`}>{s}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {detailReg.payment_screenshot_url && (
                <div className="pt-2">
                  <button
                    onClick={() => setScreenshotUrl(detailReg.payment_screenshot_url)}
                    className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs font-semibold"
                    data-testid="view-payment-screenshot-btn"
                  >
                    <Receipt size={14} weight="bold" /> View Payment Screenshot
                  </button>
                </div>
              )}
            </div>

            {(detailReg.status === 'pending_verification' || detailReg.status === 'form_submitted') && (
              <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setConfirmAction({ type: 'approve', reg: detailReg })}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <CheckCircle size={16} weight="bold" /> Approve
                </button>
                <button
                  onClick={() => setConfirmAction({ type: 'reject', reg: detailReg })}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <XCircle size={16} weight="bold" /> Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Screenshot lightbox */}
      {screenshotUrl && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setScreenshotUrl('')}>
          <div className="relative max-w-3xl max-h-[90vh] w-full flex items-center justify-center">
            {screenshotUrl.endsWith('.pdf') ? (
              <iframe src={screenshotUrl} title="Payment proof" className="w-full h-[80vh] rounded-xl bg-white" />
            ) : (
              <img src={screenshotUrl} alt="Payment screenshot" className="max-w-full max-h-[85vh] rounded-xl" data-testid="payment-screenshot-image" />
            )}
            <button
              onClick={() => setScreenshotUrl('')}
              className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-900 shadow-lg"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Confirm approve/reject */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" data-testid="confirm-action-modal">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {confirmAction.type === 'approve' ? 'Approve Registration?' : 'Reject Registration?'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {confirmAction.type === 'approve'
                ? 'The player will be notified their registration is confirmed.'
                : 'The player will be notified their registration was not approved.'}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmAction(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-xl text-sm font-semibold">Cancel</button>
              <button
                onClick={performAction}
                className={`flex-1 text-white px-4 py-2.5 rounded-xl text-sm font-semibold ${confirmAction.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="confirm-action-btn"
              >
                {confirmAction.type === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Row = ({ label, value }) => (
  value ? (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-xs uppercase font-semibold text-gray-500">{label}</div>
      <div className="col-span-2 text-gray-900 font-medium break-words">{value}</div>
    </div>
  ) : null
);

export default TournamentRegistrations;
